import { FlashList } from '@shopify/flash-list';
import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import { Animated, TouchableOpacity } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { DateRangePickerWebV3 } from '@components/DateRangePicker/DateRangePickerWebV3';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, ShadowBox, Text } from '@components/Restyle';
import { FilterSectionHeader } from '@components/shared/FilterSectionHeader';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { TeamListItem } from '@components/Team/TeamListItem';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { Team } from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import { useDateRangeString } from '@hooks/useDateRangeString';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';

export type PopupProjectsFilterV3Props = {
  onSelectOwner?: (owners: ContactType[]) => void;
  selectedOwners?: ContactType[];

  onSelectTeam?: (
    item: Pick<Team, 'id' | 'name' | 'avatar' | 'usersCount' | 'personal'>
  ) => void;
  selectedTeamIds?: string[];

  onSelectMember?: (members: ContactType[]) => void;
  selectedMembers?: ContactType[];

  onSelectDueDateRange?: (dr: DateRange) => void;
  selectedDueDateRange?: DateRange;

  selectedDueDateStrList?: string[];
  onSelectedDueDateStrList?: (x: string[]) => void;

  onClearTeams?: () => void;

  onPressSelectDates?: () => void;

  initiallyOpenSection?: 'owner' | 'team' | 'member' | 'duedate';
};

export type PopupProjectsFilterDateRangeV3Props = {
  onSelectDueDateRange?: (dr: DateRange) => void;
  selectedDueDateRange?: DateRange;
  isRangeSelector?: boolean;
};

export const PopupProjectsFilterDateRangeV3: React.FC<
  PopupProjectsFilterDateRangeV3Props
> = ({
  onSelectDueDateRange,
  selectedDueDateRange,
  isRangeSelector = false,
}) => {
  return (
    <ShadowBox
      flex={1}
      width={352}
      alignItems='center'
      justifyContent='center'
      borderRadius='xs'
      variant='base'
      backgroundColor='white'>
      <Box flex={1} style={{ width: 352 }}>
        <DateRangePickerWebV3
          isRangeSelector={isRangeSelector}
          onSelect={(d1, d2) => {
            onSelectDueDateRange?.({
              start: d1 ? dayjs(d1) : undefined,
              end: d2 ? dayjs(d2) : undefined,
            });
          }}
          start={selectedDueDateRange?.start?.toDate()}
          end={selectedDueDateRange?.end?.toDate()}
        />
      </Box>
    </ShadowBox>
  );
};

const ESTIMATED_ITEM_SIZE = 36;

const OWNER_SECTION_MAX_HEIGHT = 36 * 8 + 8;
const TEAM_SECTION_MAX_HEIGHT = 36 * 5 + 8;
const MEMBER_SECTION_MAX_HEIGHT = 36 * 5 + 8;
const DUE_DATE_SECTION_MAX_HEIGHT = 36 * 4 + 8;

export const PopupProjectsFilterV3: React.FC<PopupProjectsFilterV3Props> = ({
  onSelectOwner,
  selectedOwners = [],
  onSelectTeam,
  selectedTeamIds = [],
  onSelectMember,
  selectedMembers = [],
  onSelectDueDateRange,
  selectedDueDateRange,
  selectedDueDateStrList = [],
  onSelectedDueDateStrList,
  onClearTeams,
  onPressSelectDates,
  initiallyOpenSection = 'owner',
}) => {
  const { contacts } = useContacts();
  const { me } = useMe();
  const { teams } = useListTeamsFromQuery();

  const contactsAndMe =
    !me || contacts.some((c) => c.id === me.id) ? contacts : [...contacts, me];

  const listUsersToContacts = convertUsersToContacts(contactsAndMe);

  const [search, setSearch] = useState<string>('');
  const [hoveredOwnerId, setHoveredOwnerId] = useState('');
  const [hoveredTeamId, setHoveredTeamId] = useState('');
  const [hoveredMemberId, setHoveredMemberId] = useState('');
  const [hoveredDueDateItem, setHoveredDueDateItem] = useState('');

  const selectedDateRangeStr = useDateRangeString({ selectedDueDateRange });

  const sortedOwnerContacts = [...listUsersToContacts]
    .filter(
      (u) =>
        search.length === 0 ||
        u.displayName.toLowerCase().includes(search.toLowerCase().trim())
    )
    .sort((a, b) => (a.firstName ?? '')?.localeCompare(b.firstName ?? ''));

  const sortedMemberContacts = [...listUsersToContacts]
    .filter(
      (u) =>
        search.length === 0 ||
        u.displayName.toLowerCase().includes(search.toLowerCase().trim())
    )
    .sort((a, b) => (a.firstName ?? '')?.localeCompare(b.firstName ?? ''));

  const filteredTeams =
    teams?.filter((t) =>
      t.name.trim().toLowerCase().includes(search.trim().toLowerCase())
    ) || [];

  const onPressOwner = (contact: ContactType) => {
    const isIn = selectedOwners.some((c) => c.id === contact.id);
    const updatedSelected = isIn
      ? selectedOwners.filter((c) => c.id !== contact.id)
      : [...selectedOwners, contact];
    onSelectOwner?.(updatedSelected);
  };

  const onPressMember = (contact: ContactType) => {
    const isIn = selectedMembers.some((c) => c.id === contact.id);
    const updatedSelected = isIn
      ? selectedMembers.filter((c) => c.id !== contact.id)
      : [...selectedMembers, contact];
    onSelectMember?.(updatedSelected);
  };

  const ownerMaxHeight = Math.max(
    44,
    Math.min(sortedOwnerContacts.length * 36 + 8, OWNER_SECTION_MAX_HEIGHT)
  );
  const teamMaxHeight = Math.max(
    44,
    Math.min(filteredTeams.length * 36 + 8, TEAM_SECTION_MAX_HEIGHT)
  );
  const memberMaxHeight = Math.max(
    44,
    Math.min(sortedMemberContacts.length * 36 + 8, MEMBER_SECTION_MAX_HEIGHT)
  );

  const [isOwnerOpen, setIsOwnerOpen] = useState(
    initiallyOpenSection === 'owner'
  );
  const [isTeamOpen, setIsTeamOpen] = useState(initiallyOpenSection === 'team');
  const [isMemberOpen, setIsMemberOpen] = useState(
    initiallyOpenSection === 'member'
  );
  const [isDueDateOpen, setIsDueDateOpen] = useState(
    initiallyOpenSection === 'duedate'
  );

  const ownerAnim = useRef(
    new Animated.Value(isOwnerOpen ? ownerMaxHeight : 0)
  ).current;
  const ownerIconAnim = useRef(new Animated.Value(isOwnerOpen ? 1 : 0)).current;

  const teamAnim = useRef(
    new Animated.Value(isTeamOpen ? teamMaxHeight : 0)
  ).current;
  const teamIconAnim = useRef(new Animated.Value(isTeamOpen ? 1 : 0)).current;

  const memberAnim = useRef(
    new Animated.Value(isMemberOpen ? memberMaxHeight : 0)
  ).current;
  const memberIconAnim = useRef(
    new Animated.Value(isMemberOpen ? 1 : 0)
  ).current;

  const dueDateAnim = useRef(
    new Animated.Value(isDueDateOpen ? DUE_DATE_SECTION_MAX_HEIGHT : 0)
  ).current;
  const dueDateIconAnim = useRef(
    new Animated.Value(isDueDateOpen ? 1 : 0)
  ).current;

  const animateSection = (
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    heightAnim: Animated.Value,
    maxHeight: number,
    iconAnim: Animated.Value
  ) => {
    setIsOpen((prev) => {
      const next = !prev;
      const heightToValue = next ? maxHeight : 0;
      const iconToValue = next ? 1 : 0;

      Animated.timing(heightAnim, {
        toValue: heightToValue,
        duration: 200,
        useNativeDriver: false,
      }).start();

      Animated.timing(iconAnim, {
        toValue: iconToValue,
        duration: 200,
        useNativeDriver: true,
      }).start();

      return next;
    });
  };

  const toggleOwnerSection = () => {
    if (!isOwnerOpen) {
      if (isTeamOpen) {
        toggleTeamSection();
      }
      if (isMemberOpen) {
        toggleMemberSection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }
    animateSection(setIsOwnerOpen, ownerAnim, ownerMaxHeight, ownerIconAnim);
  };

  const toggleTeamSection = () => {
    if (!isTeamOpen) {
      if (isOwnerOpen) {
        toggleOwnerSection();
      }
      if (isMemberOpen) {
        toggleMemberSection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }
    animateSection(setIsTeamOpen, teamAnim, teamMaxHeight, teamIconAnim);
  };

  const toggleMemberSection = () => {
    if (!isMemberOpen) {
      if (isOwnerOpen) {
        toggleOwnerSection();
      }
      if (isTeamOpen) {
        toggleTeamSection();
      }
      if (isDueDateOpen) {
        toggleDueDateSection();
      }
    }
    animateSection(
      setIsMemberOpen,
      memberAnim,
      memberMaxHeight,
      memberIconAnim
    );
  };

  const toggleDueDateSection = () => {
    if (!isDueDateOpen) {
      if (isOwnerOpen) {
        toggleOwnerSection();
      }
      if (isTeamOpen) {
        toggleTeamSection();
      }
      if (isMemberOpen) {
        toggleMemberSection();
      }
    }
    animateSection(
      setIsDueDateOpen,
      dueDateAnim,
      DUE_DATE_SECTION_MAX_HEIGHT,
      dueDateIconAnim
    );
  };

  return (
    <ShadowBox
      flex={1}
      width={380}
      alignItems='center'
      justifyContent='center'
      borderRadius='xs'
      variant='base'
      backgroundColor='white'>
      <>
        <Box
          marginHorizontal='m'
          marginTop='m'
          style={{ width: 348, backgroundColor: 'grey02' }}
          justifyContent='center'
          flexDirection='row'
          alignItems='flex-start'>
          <SearchInput
            value={search}
            height={40}
            onTextChanged={(value: string) => {
              setSearch(value);
            }}
            placeHolder='Search'
          />
        </Box>

        <Box style={{ width: 380 }} mt='s'>
          <FilterSectionHeader
            title='Owner'
            badgeNumber={selectedOwners.length}
            onPress={toggleOwnerSection}
            iconAnim={ownerIconAnim}
            wrapEntireRow={false}
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: ownerAnim,
            }}>
            <Box pb='xs' flex={1}>
              {sortedOwnerContacts.length === 0 ? (
                <Box
                  mx='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              ) : (
                <FlashList
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  data={sortedOwnerContacts}
                  renderItem={({ item: contact }) => {
                    const { id, avatar, displayName, name } = contact;
                    return (
                      <Box
                        height={36}
                        justifyContent='center'
                        accessibilityLabel='Owner Item'
                        style={{
                          backgroundColor:
                            hoveredOwnerId !== '' && hoveredOwnerId === id
                              ? theme.colors.grey01
                              : theme.colors.white,
                        }}
                        onMouseEnter={() => {
                          setHoveredOwnerId(id);
                        }}
                        onMouseLeave={() => {
                          setHoveredOwnerId('');
                        }}>
                        <TouchableOpacity
                          onPress={() => {
                            onPressOwner(contact);
                          }}
                          accessibilityLabel={
                            displayName ? displayName : 'No Name'
                          }>
                          <Box
                            flexDirection='row'
                            alignItems='center'
                            justifyContent='space-between'>
                            <Box
                              ml='m'
                              justifyContent='center'
                              marginRight='xs'>
                              <CheckBoxButton
                                checked={selectedOwners?.some(
                                  (s) => s.id === contact.id
                                )}
                              />
                            </Box>
                            <Avatar
                              id={id}
                              label={name}
                              avatar={avatar}
                              size='small'
                            />
                            <Box mr='m' marginLeft='xs' flex={1}>
                              <Text
                                variant='webBodySecondary'
                                color={
                                  displayName ? 'textSecondary' : 'grey04'
                                }>
                                {displayName ? (
                                  <Highlighter
                                    highlightStyle={{
                                      backgroundColor:
                                        theme.colors.yellowBright,
                                    }}
                                    autoEscape
                                    searchWords={search?.split(' ') || ''}
                                    textToHighlight={displayName}
                                  />
                                ) : (
                                  'No name'
                                )}
                              </Text>
                            </Box>
                          </Box>
                        </TouchableOpacity>
                      </Box>
                    );
                  }}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        <Box style={{ width: 380 }}>
          <FilterSectionHeader
            title='Team'
            badgeNumber={selectedTeamIds.length}
            onPress={toggleTeamSection}
            iconAnim={teamIconAnim}
            wrapEntireRow={false}
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: teamAnim,
            }}>
            <Box pb='xs' flex={1}>
              {filteredTeams.length === 0 ? (
                <Box
                  mx='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              ) : (
                <FlashList
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  data={filteredTeams}
                  keyExtractor={(item) => item?.id}
                  renderItem={({ item }) => (
                    <Box
                      height={36}
                      justifyContent='center'
                      accessibilityLabel='Team Item'
                      style={{
                        backgroundColor:
                          hoveredTeamId !== '' && hoveredTeamId === item?.id
                            ? theme.colors.grey01
                            : theme.colors.white,
                      }}
                      onMouseEnter={() => {
                        setHoveredTeamId(item?.id);
                      }}
                      onMouseLeave={() => {
                        setHoveredTeamId('');
                      }}>
                      <Box px='m'>
                        <TeamListItem
                          isSelectable={true}
                          filterVal={search}
                          isSmall
                          iconSize='l'
                          team={item}
                          onSelect={() => {
                            onSelectTeam?.(item);
                          }}
                          hideTrailCheckmark={true}
                          isSelected={selectedTeamIds.some(
                            (t) => t === item.id
                          )}
                        />
                      </Box>
                    </Box>
                  )}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        <Box style={{ width: 380 }}>
          <FilterSectionHeader
            title='Member'
            badgeNumber={selectedMembers.length}
            onPress={toggleMemberSection}
            iconAnim={memberIconAnim}
            wrapEntireRow={false}
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: memberAnim,
            }}>
            <Box pb='xs' flex={1}>
              {sortedMemberContacts.length === 0 ? (
                <Box
                  mx='m'
                  height={48}
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='webBodySecondary'
                    color='grey04'
                    textAlign='center'>
                    No results found
                  </Text>
                </Box>
              ) : (
                <FlashList
                  showsVerticalScrollIndicator={false}
                  showsHorizontalScrollIndicator={false}
                  data={sortedMemberContacts}
                  renderItem={({ item: contact }) => {
                    const { id, avatar, displayName, name } = contact;
                    return (
                      <Box
                        height={36}
                        justifyContent='center'
                        accessibilityLabel='Member Item'
                        style={{
                          backgroundColor:
                            hoveredMemberId !== '' && hoveredMemberId === id
                              ? theme.colors.grey01
                              : theme.colors.white,
                        }}
                        onMouseEnter={() => {
                          setHoveredMemberId(id);
                        }}
                        onMouseLeave={() => {
                          setHoveredMemberId('');
                        }}>
                        <TouchableOpacity
                          onPress={() => {
                            onPressMember(contact);
                          }}
                          accessibilityLabel={
                            displayName ? displayName : 'No Name'
                          }>
                          <Box
                            flexDirection='row'
                            alignItems='center'
                            justifyContent='space-between'>
                            <Box
                              ml='m'
                              justifyContent='center'
                              marginRight='xs'>
                              <CheckBoxButton
                                checked={selectedMembers?.some(
                                  (s) => s.id === contact.id
                                )}
                              />
                            </Box>
                            <Avatar
                              id={id}
                              label={name}
                              avatar={avatar}
                              size='small'
                            />
                            <Box mr='m' marginLeft='xs' flex={1}>
                              <Text
                                variant='webBodySecondary'
                                color={
                                  displayName ? 'textSecondary' : 'grey04'
                                }>
                                {displayName ? (
                                  <Highlighter
                                    highlightStyle={{
                                      backgroundColor:
                                        theme.colors.yellowBright,
                                    }}
                                    autoEscape
                                    searchWords={search?.split(' ') || ''}
                                    textToHighlight={displayName}
                                  />
                                ) : (
                                  'No name'
                                )}
                              </Text>
                            </Box>
                          </Box>
                        </TouchableOpacity>
                      </Box>
                    );
                  }}
                  estimatedItemSize={ESTIMATED_ITEM_SIZE}
                />
              )}
            </Box>
          </Animated.View>
        </Box>

        <Box style={{ width: 380 }}>
          <FilterSectionHeader
            title='Due Date'
            badgeNumber={
              selectedDueDateStrList.length +
              (selectedDueDateRange?.start || selectedDueDateRange?.end ? 1 : 0)
            }
            onPress={toggleDueDateSection}
            iconAnim={dueDateIconAnim}
            wrapEntireRow={false}
          />

          <Animated.View
            style={{
              overflow: 'hidden',
              height: dueDateAnim,
            }}>
            <Box pb='xs' flex={1}>
              <FlashList
                showsVerticalScrollIndicator={false}
                showsHorizontalScrollIndicator={false}
                data={[
                  'Today',
                  'Tomorrow',
                  'Next 3 days',
                  selectedDateRangeStr ?? 'Select Dates',
                ]}
                renderItem={({ item, index }) => {
                  const isHovered = hoveredDueDateItem === item;
                  const isSelectDates = index === 3;
                  const isChecked =
                    !isSelectDates &&
                    selectedDueDateStrList?.some((s) => s === item);

                  return (
                    <Box
                      height={36}
                      justifyContent='center'
                      accessibilityLabel='DueDate Item'
                      style={{
                        backgroundColor: isHovered
                          ? theme.colors.grey01
                          : theme.colors.white,
                      }}
                      onMouseEnter={() => {
                        setHoveredDueDateItem(item);
                      }}
                      onMouseLeave={() => {
                        setHoveredDueDateItem('');
                      }}>
                      <TouchableOpacity
                        onPress={() => {
                          if (isSelectDates) {
                            onPressSelectDates?.();
                          } else {
                            onSelectDueDateRange?.({
                              start: undefined,
                              end: undefined,
                            });
                            onSelectedDueDateStrList?.([item]);
                          }
                        }}
                        accessibilityLabel={item}>
                        <Box
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='space-between'>
                          <Box ml='m' justifyContent='center' marginRight='xs'>
                            {isSelectDates ? (
                              <Icon
                                name='Calendar'
                                color={
                                  isSelectDates && selectedDateRangeStr
                                    ? 'greenSecondary'
                                    : 'grey05'
                                }
                                variant='m'
                              />
                            ) : (
                              <RadioButton
                                iconVariant='m'
                                onPress={() => {
                                  onSelectDueDateRange?.({
                                    start: undefined,
                                    end: undefined,
                                  });
                                  onSelectedDueDateStrList?.([item]);
                                }}
                                isSelected={isChecked}
                              />
                            )}
                          </Box>

                          <Box mr='m' flex={1}>
                            <Text
                              variant='webBodySecondary'
                              color={
                                isSelectDates && selectedDateRangeStr
                                  ? 'greenSecondary'
                                  : 'textSecondary'
                              }>
                              {item}
                            </Text>
                          </Box>
                        </Box>
                      </TouchableOpacity>
                    </Box>
                  );
                }}
                estimatedItemSize={ESTIMATED_ITEM_SIZE}
              />
            </Box>
          </Animated.View>
        </Box>

        <Box
          width={380}
          borderTopWidth={1}
          borderTopColor='grey02'
          height={48}
          alignItems='center'
          justifyContent='center'>
          <TouchableOpacity
            onPress={() => {
              onSelectOwner?.([]);
              onSelectMember?.([]);
              onClearTeams?.();
              onSelectedDueDateStrList?.([]);
              onSelectDueDateRange?.({});
            }}>
            <Text
              flex={1}
              variant='webBodySecondary'
              color={
                selectedOwners.length > 0 ||
                selectedMembers.length > 0 ||
                selectedTeamIds.length > 0 ||
                selectedDueDateStrList.length > 0 ||
                selectedDueDateRange?.start ||
                selectedDueDateRange?.end
                  ? 'textPrimary'
                  : 'grey04'
              }>
              Clear All
            </Text>
          </TouchableOpacity>
        </Box>
      </>
    </ShadowBox>
  );
};
